// Copyright (c) 2022 by Move Closer

import { MappingConfig } from '@movecloser/front-core'

import { logger } from '../../../support'

import {
  AbstractSetDriver,
  AbstractTypeDriver,
  AnyDescription,
  DriverConfig,
  RelatedRecord,
  RelatedType,
  ResolvesDriver,
  SetDescription
} from '../../shared'
import { FileRelatedType } from '../../media/related.types'

import { CategoryRelatedType } from './category'
import { DescriptionOfProduct, ProductData, Variant, VariantSelector, VariantSelectorAttributes } from '../contracts'
import { productAdapterMap } from '../adapters'
import { ImageData } from '../../media'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export class ProductDriver extends AbstractTypeDriver<DescriptionOfProduct, ProductData> {
  protected adapterMap: MappingConfig = productAdapterMap
  protected dependencies: RelatedType[] = [CategoryRelatedType, FileRelatedType]
  protected key: RelatedType = ProductRelatedType

  /**
   * @inheritDoc
   */
  protected resolveNested (
    unresolved: DescriptionOfProduct,
    record: RelatedRecord,
    config: DriverConfig,
    driverResolver?: ResolvesDriver
  ): AnyDescription {
    if (typeof driverResolver === 'undefined') {
      throw new Error('[ProductRelatedType]: Missing driverResolver.')
    }

    let category
    try {
      const categoryDriver = driverResolver(CategoryRelatedType)
      category = categoryDriver.resolve(`${unresolved.category.value}`, record, config, driverResolver)
    } catch (e) {
      logger(e, 'warn')
    }

    const fileDriver = driverResolver(FileRelatedType)
    const variants: Record<string, Variant<string>> = {}

    for (const v in unresolved.variants) {
      const files: ImageData[] = []
      const variant = unresolved.variants[v]

      for (let j = 0; j < variant.images.length; j++) {
        try {
          files.push(fileDriver.resolve(`${variant.images[j].value}`, record, config) as ImageData)
        } catch (e) {
          logger(e, 'warn')
        }
      }

      variants[v] = { ...variant, images: files }
    }

    const variantSelector: VariantSelector = {}

    for (const attributeName in unresolved.variantSelector) {
      const attributeValues = unresolved.variantSelector[attributeName]
      const resolvedAttributeValues: VariantSelectorAttributes[] = []

      for (const attributeValue of attributeValues) {
        if ('value' in attributeValue && typeof attributeValue.value === 'object') {
          try {
            resolvedAttributeValues.push({
              ...attributeValue,
              value: fileDriver.resolve(`${attributeValue.value.value}`, record, config) as ImageData
            })
          } catch (e) {
            resolvedAttributeValues.push({
              ...attributeValue,
              value: 'transparent'
            })
            logger(e, 'warn')
          }
        } else {
          resolvedAttributeValues.push(attributeValue as VariantSelectorAttributes)
        }
      }

      variantSelector[attributeName] = resolvedAttributeValues
    }

    let image
    if (unresolved.image) {
      try {
        image = fileDriver.resolve(`${unresolved.image.value}`, record, config)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    let specification: AnyDescription = unresolved.specification

    if (specification && specification.heroSection && specification.heroSection.cover) {
      try {
        const cover = fileDriver.resolve(`${specification.heroSection.cover.value}`, record, config)

        specification = {
          ...specification,
          heroSection: {
            ...specification.heroSection,
            cover
          }
        }
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (specification && specification.stepSection && specification.stepSection.image) {
      try {
        const image = fileDriver.resolve(`${specification.stepSection.image.value}`, record, config)

        specification = {
          ...specification,
          stepSection: {
            ...specification.stepSection,
            image
          }
        }
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (specification && specification.columnSection && specification.columnSection.image) {
      try {
        const image = fileDriver.resolve(`${specification.columnSection.image.value}`, record, config)

        specification = {
          ...specification,
          columnSection: {
            ...specification.columnSection,
            image
          }
        }
      } catch (e) {
        logger(e, 'warn')
      }
    }

    return {
      ...unresolved,
      image,
      specification,
      variants,
      variantSelector,
      category
    }
  }
}

export const ProductRelatedType: RelatedType = 'product'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export class ProductsDriver extends AbstractSetDriver<SetDescription, ProductData> {
  protected childType: RelatedType = ProductRelatedType
  protected dependencies: RelatedType[] = [ProductRelatedType]
  protected key: RelatedType = ProductsRelatedType
}

export const ProductsRelatedType: RelatedType = 'products'
